import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { SOCKET_TOURNAMENT_UPDATE } from '../../config/constants';
import { analytics } from '../../services/analytics';
import type { EventAction, WidgetEventOptionalArgs } from '../../services/analytics/types';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { devError } from '../../utils/log';
import { type CoinType } from '../cointype/CoinTypeStore';
import type {
  FeatureFetchStatus,
  FeatureFetchType,
  FeatureManagerDialogType,
  IFeatureManagerStore,
} from '../feature-manager/types';
import type { GameLabel } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { selectPlayerTournament } from './selectPlayerTournament';
import { TournamentOfChampionsState } from './types';
import type {
  Tournament,
  TournamentEventParams,
  TournamentEventShownBy,
  TournamentResponse,
  TournamentV2Response,
} from './types';

export const TOURNAMENT_FETCH_COALESCE_MS = 750;

export class TournamentStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    this.tournamentsFlag = resolveFlag('tournaments', true);
    this.enablePreviousTournamentFlag = resolveFlag('enable_previous_tournament_logic', true);

    ldClient?.on('ready', () => {
      this.setTournamentsFlag(ldClient?.variation('tournaments', true));
      this.enablePreviousTournamentFlag = ldClient?.variation('enable_previous_tournament_logic', true);
    });

    ldClient?.on('change', (changes) => {
      if ('tournaments' in changes) {
        this.updateTournamentsFlag(changes.tournaments.current);
      }
    });

    if (this.tournamentsFlag) {
      socket.on(SOCKET_TOURNAMENT_UPDATE, this.subscribeToSocket.bind(this));
    }
  }

  @observable
  public tournamentsFlag: boolean = false;

  @observable
  public enablePreviousTournamentFlag: boolean = false;

  @observable
  public currentTournament: Tournament | null = null;

  @observable
  public loading: Record<string, Record<string, boolean>> = {
    GC: {},
    SC: {},
  };

  @observable
  public tournamentGameLabel: GameLabel | null = null;

  @computed
  public get isTournamentDialogOpen() {
    return this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenTournamentDialog;
  }

  @computed
  public get hasLeaderboard() {
    const { coinType } = this.rootStore.coinTypeStore;
    return !!this.currentTournament?.leaderboards[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @action
  private setTournamentsFlag(value: boolean) {
    this.tournamentsFlag = value;
  }

  @action
  public setLoading(key: string, loading: boolean, coinType?: CoinType) {
    const coinTypeValue = coinType || this.rootStore.coinTypeStore.coinType;
    this.loading = {
      ...this.loading,
      [coinTypeValue]: {
        ...this.loading[coinTypeValue],
        [key]: loading,
      },
    };
  }

  @action
  public getLoading(key: string) {
    const { coinType } = this.rootStore.coinTypeStore;
    return this.loading[coinType][key];
  }

  @action
  private setCurrentTournament(tournament: Tournament | null) {
    this.currentTournament = tournament;
  }

  @action
  public setTournamentGameLabel(gameLabel: GameLabel | null) {
    this.tournamentGameLabel = gameLabel;
  }

  @action clearPlayerCurrentTournament() {
    this.setCurrentTournament(null);
    this.closeTournamentDialog();
  }

  @action
  public reset() {
    this.currentTournament = null;
    this.tournamentGameLabel = null;
    this.loading = { GC: {}, SC: {} };
    this.isFirstTimeOpen = { GC: false, SC: false };
    this.inflightByCoinType = {};
  }

  @action
  public closeTournamentDialog() {
    if (this.isTournamentDialogOpen) {
      this.rootStore.uiStore.setCloseDialog();
      if (this.currentTournament) {
        if (this.currentTournament.status === 'finished') {
          this.rootStore.featureManagerStore.onDialogFinished('PostTournament');
        } else {
          this.rootStore.featureManagerStore.onDialogFinished('Tournament');
        }
      }
    }
  }

  public updateTournamentsFlag(value: boolean) {
    if (this.tournamentsFlag === value) return;

    this.setTournamentsFlag(value);

    const subscription = this.subscribeToSocket.bind(this);

    if (this.tournamentsFlag) {
      socket.on(SOCKET_TOURNAMENT_UPDATE, subscription);
    } else {
      socket.off(SOCKET_TOURNAMENT_UPDATE, subscription);
    }
  }

  private updateTournamentByType(data: Tournament | null, coinType: 'gc' | 'sc') {
    if (!data) {
      this.setCurrentTournament(null);
      return;
    }
    if (!this.currentTournament) {
      this.setCurrentTournament(data);
      return;
    }
    this.setCurrentTournament({
      ...data,
      leaderboards: {
        ...this.currentTournament.leaderboards,
        [coinType]: data.leaderboards?.[coinType],
      },
    });
  }

  public async startTournamentForPlayer() {
    try {
      if (!this.currentTournament) {
        devError('No current tournament');
        return;
      }

      this.setLoading('startTournamentForPlayer', true);
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/start/${this.currentTournament.key}/${coinType}`);
      // Forced: an older in-flight fetch must not stand in for the post-start state.
      await this.fetch(undefined, { force: true });
    } finally {
      this.setLoading('startTournamentForPlayer', false);
    }
  }

  public async finishTournamentForPlayer() {
    try {
      if (!this.currentTournament) {
        devError('No current tournament');
        return;
      }

      this.setLoading('finishTournamentForPlayer', true);
      const coinType = this.rootStore.coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';
      await postRequest(`tournament/finish/${this.currentTournament?.key}/${coinType}`);
    } finally {
      this.setLoading('finishTournamentForPlayer', false);
    }
  }

  public handleAnalytics({
    event_action = 'click',
    params,
  }: {
    event_action: EventAction;
    params?: TournamentEventParams & WidgetEventOptionalArgs;
  }) {
    try {
      const event = {
        tournament_id: this.currentTournament?.key,
        game_id: this.currentTournament?.tournamentGroup.gameId,
        tournament_status: this.currentTournament?.status,
        tournament_type: this.currentTournament?.tournamentGroup?.type,
        ...this.setPrizeForAnalytics(),
        ...params,
      };
      const { Tournament, PostTournament } = this.rootStore.featureManagerStore.dialogManagerData || {};
      const isPostTournament = this.currentTournament?.status === 'finished';
      const feature_order = (isPostTournament ? PostTournament?.order : Tournament?.order) || 0;
      const feature_sub_type = isPostTournament ? 'post_tournament' : 'tournament';

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      void analytics.sendEvent('widget', {
        event_action,
        feature_order,
        feature_type: 'tournament',
        feature_sub_type,
        ...event,
      });
    } catch (e) {
      devError('handleAnalytics', e);
    }
  }

  private setPrizeForAnalytics() {
    if (!this.currentTournament) {
      devError('No current tournament');
      return;
    }

    const coin_type = this.rootStore.coinTypeStore.coinType === 'GC' ? 'cc' : 'sc';
    if (coin_type === 'cc') {
      const gcPlayerPosition = this.currentTournament.leaderboards.gc.player.position;
      const gcPlayerPrize = this.currentTournament.leaderboards.gc.player.prize;
      const gcPrizePlain = gcPlayerPrize ? toJS(gcPlayerPrize) : null;
      const score = this.currentTournament.leaderboards.gc.player.score;

      return {
        position: !isNil(gcPlayerPosition) ? gcPlayerPosition + 1 : null,
        prize: gcPrizePlain,
        coin_type,
        score,
      };
    }

    const scPlayerPosition = this.currentTournament.leaderboards.sc.player.position;
    const scPlayerPrize = this.currentTournament.leaderboards.sc.player.prize;
    const scPrizePlain = scPlayerPrize ? toJS(scPlayerPrize) : null;
    const score = this.currentTournament.leaderboards.sc.player.score;

    return {
      position: !isNil(scPlayerPosition) ? scPlayerPosition + 1 : null,
      prize: scPrizePlain,
      coin_type,
      score,
    };
  }

  private openDialog(triggeredBy: TournamentEventShownBy) {
    this.rootStore.uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: {
        shown_by: triggeredBy,
      },
    });
  }

  private isFirstTimeOpen: Record<CoinType, boolean> = {
    GC: false,
    SC: false,
  };

  private setFirstTimeOpen(coinType: CoinType) {
    this.isFirstTimeOpen[coinType] = true;
  }

  public dialogTypes: FeatureManagerDialogType[] = ['Tournament', 'PostTournament'];

  public startDialogs(dialogType?: FeatureManagerDialogType) {
    if (dialogType === 'PostTournament') {
      const tocStore = this.rootStore.tournamentOfChampionsStore;
      const toc = tocStore.tournamentOfChampions;
      const isChampionForSummary = toc?.state === TournamentOfChampionsState.CHAMPIONS_COMPLETED && !toc.isEliminated;
      // A round whose claim just failed waits for the widget or the next lobby fetch.
      if (tocStore.ownsCurrentTournamentSlot && tocStore.isPendingRoundAutoOpenHeld) return false;
      // Summary finished this session (dismiss POST may be in flight), or a settled one-round event (no Summary).
      if (
        isChampionForSummary &&
        tocStore.ownsCurrentTournamentSlot &&
        (tocStore.isChampionsSummaryDismissed || tocStore.isSettledOneRoundChampion)
      ) {
        return false;
      }
      // Only claim the dialog for the champion summary when TOC actually owns the slot — otherwise
      // an unrelated, unclaimed regular tournament owns it, and this would auto-open its dialog
      // instead (see TournamentOfChampionsStore.autoOpenPendingPopup for the same guard).
      if (isChampionForSummary && tocStore.ownsCurrentTournamentSlot) {
        this.openDialog('log_in');
        return true;
      }
    }

    const coinType = this.rootStore.coinTypeStore.coinType;
    const isFirstTimeOpen = this.isFirstTimeOpen[coinType];
    if (this.currentTournament && this.hasLeaderboard) {
      if (dialogType === 'Tournament') {
        if (coinType === 'GC') {
          return false;
        }

        // ToC rounds after the first open once per round (per device) instead of on every pending login.
        const tocStore = this.rootStore.tournamentOfChampionsStore;
        if (tocStore.isInLaterRound) {
          return tocStore.tryAutoOpenRoundStart();
        }

        switch (this.currentTournament.status) {
          case 'upcoming':
            // don't auto open for GC upcoming tournaments, open once for SC
            if (!isFirstTimeOpen) {
              this.openDialog('log_in');
              this.setFirstTimeOpen(coinType);
              return true;
            }
            return false;
          case 'ongoing':
            if (this.currentTournament.playerStatus === 'pending') {
              this.openDialog('log_in');
              return true;
            }
            return false;
          case 'finished':
            return false;
          default:
            return false;
        }
      } else if (dialogType === 'PostTournament') {
        const isTournamentFinishedForPlayer =
          this.currentTournament.status === 'finished' && this.currentTournament.playerStatus === 'started';
        if (isTournamentFinishedForPlayer) {
          this.openDialog('log_in');
          return true;
        }
      }
    }

    return false;
  }

  // Both fetches let a rejection through on purpose. Catching here and returning an all-null
  // payload is what made a 500 indistinguishable from "the player has no tournament": the store
  // cleared `currentTournament`, and the race widget, dialog and position went with it while the
  // round was still live. `performFetch`'s own catch turns a rejection into 'ERROR' and writes
  // nothing, so whatever is already rendered survives until the next poll.
  private async fetchTournamentV2(coinType: 'gc' | 'sc') {
    const res = await getRequest<{ data: TournamentV2Response }>(`/v2/tournament/current/${coinType}`);

    return this.rootStore.tournamentOfChampionsStore.normalizeV2TournamentsPayload(res.data.tournaments);
  }

  private async fetchTournament(coinType: 'gc' | 'sc') {
    const mockPayload = this.rootStore.tournamentOfChampionsStore.getMockFetchPayload();
    if (mockPayload) {
      return mockPayload;
    }

    if (this.enablePreviousTournamentFlag) {
      return this.fetchTournamentV2(coinType);
    }

    const res = await getRequest<{ data: TournamentResponse }>(`tournament/current/${coinType}`);

    return {
      currentTournament: res.data.tournament,
      previousTournament: null,
      tournamentOfChampions: null,
    };
  }

  private inflightByCoinType: Partial<Record<CoinType, Promise<FeatureFetchStatus>>> = {};
  private lastResultByCoinType: Partial<Record<CoinType, { at: number; status: FeatureFetchStatus }>> = {};

  public fetch(
    featureManagerOptions?: { fetchType: FeatureFetchType },
    options?: { force?: boolean; maxAgeMs?: number },
  ): Promise<FeatureFetchStatus> {
    if (!this.tournamentsFlag) return Promise.resolve('OFF');

    const { coinType } = this.rootStore.coinTypeStore;

    const inflight = this.inflightByCoinType[coinType];
    if (inflight) {
      // `force` never joins an older request: it waits for it, then sends (or joins) a newer one, never in parallel.
      return options?.force ? inflight.then(() => this.joinOrStartFetch(featureManagerOptions)) : inflight;
    }

    if (!options?.force) {
      const last = this.lastResultByCoinType[coinType];
      const maxAgeMs = options?.maxAgeMs ?? TOURNAMENT_FETCH_COALESCE_MS;
      // An error stays only for the coalescing window, so the next caller retries it.
      const reuseForMs = last?.status === 'ERROR' ? Math.min(maxAgeMs, TOURNAMENT_FETCH_COALESCE_MS) : maxAgeMs;
      if (last && Date.now() - last.at < reuseForMs) {
        return Promise.resolve(last.status);
      }
    }

    return this.startFetch(featureManagerOptions, coinType);
  }

  // After a forced wait: joins a request started after the older one settled, or sends one.
  private joinOrStartFetch(featureManagerOptions?: { fetchType: FeatureFetchType }): Promise<FeatureFetchStatus> {
    if (!this.tournamentsFlag) return Promise.resolve('OFF');
    const { coinType } = this.rootStore.coinTypeStore;
    return this.inflightByCoinType[coinType] ?? this.startFetch(featureManagerOptions, coinType);
  }

  private startFetch(
    featureManagerOptions: { fetchType: FeatureFetchType } | undefined,
    coinType: CoinType,
  ): Promise<FeatureFetchStatus> {
    const promise = this.performFetch(featureManagerOptions, coinType)
      .then((status) => {
        this.lastResultByCoinType[coinType] = { at: Date.now(), status };
        return status;
      })
      .finally(() => {
        delete this.inflightByCoinType[coinType];
      });
    this.inflightByCoinType[coinType] = promise;
    return promise;
  }

  private async performFetch(
    featureManagerOptions: { fetchType: FeatureFetchType } | undefined,
    coinType: CoinType,
  ): Promise<FeatureFetchStatus> {
    this.setLoading('fetchPlayerCurrentTournament', true, coinType);
    try {
      const lowercasedCoinType = coinType.toLowerCase() as 'gc' | 'sc';
      const res = await this.fetchTournament(lowercasedCoinType);

      // Drop writes if the user toggled coin during the in-flight request — the active coin's
      // own fetch is the source of truth for currentTournament, which is a single shared field.
      if (this.rootStore.coinTypeStore.coinType !== coinType) {
        return 'OFF';
      }

      const { currentTournament, previousTournament, tournamentOfChampions } = res;

      this.rootStore.tournamentOfChampionsStore.setTocData(tournamentOfChampions);

      const bracketSelection = selectPlayerTournament({
        currentTournament,
        previousTournament,
        tournamentOfChampions,
        coinType: lowercasedCoinType,
      });

      let tournament: Tournament | null | undefined;

      if (bracketSelection === undefined) {
        this.clearPlayerCurrentTournament();
        tournament = null;
      } else if (bracketSelection === null) {
        this.setCurrentTournament(null);
        tournament = null;
      } else {
        tournament = bracketSelection;
      }

      if (!tournament && !this.rootStore.tournamentOfChampionsStore.isFetchReadyWithTocOnly) {
        return 'OFF';
      }

      if (tournament) {
        const hasStarted = dayjs().isAfter(dayjs(tournament.tournamentGroup.startDate));

        if (hasStarted && tournament.status === 'upcoming') {
          this.clearPlayerCurrentTournament();
        } else {
          this.updateTournamentByType(tournament, lowercasedCoinType);
        }

        const isPlayerPending = tournament.playerStatus === 'pending';
        const isTournamentFinishedForPlayer = tournament.status === 'finished' && tournament.playerStatus === 'started';

        if (isPlayerPending || isTournamentFinishedForPlayer) {
          const dialogTypes = [
            isTournamentFinishedForPlayer ? 'PostTournament' : 'Tournament',
          ] as FeatureManagerDialogType[];

          if (!featureManagerOptions && !this.isTournamentDialogOpen) {
            this.rootStore.featureManagerStore.addDialogsToSequenceQueue(dialogTypes);
          }
        }
        if (tournament.gameLabel) {
          this.setTournamentGameLabel(tournament.gameLabel);
        }
      }

      // Runs after currentTournament is settled for this fetch, not from within setTocData
      // (called above) — otherwise the ownership check it makes would still see last cycle's
      // stale currentTournament instead of this response's.
      this.rootStore.tournamentOfChampionsStore.checkAutoOpenPendingPopup({ lobbyFetch: !!featureManagerOptions });
      // Login fetches go through the feature-manager sequence (startDialogs) instead.
      if (!featureManagerOptions) {
        this.rootStore.tournamentOfChampionsStore.checkAutoOpenRoundStart();
      }

      return 'READY';
    } catch (error) {
      devError('FetchTournamentError', JSON.stringify({ error }));
      return 'ERROR';
    } finally {
      this.setLoading('fetchPlayerCurrentTournament', false, coinType);
    }
  }

  private subscribeToSocket(_socketData: any) {}
}
